'use client';

import { type ReactElement, useState } from 'react';
import { AlarmClock, ExtensionBase, PlusSmBase, Repeat } from '@doist/icons/brand';
import { Box, Quote } from '@doist/marketist';
import { useTranslation } from 'app/i18n/client';
import { FeatureRadioGroup } from 'components/feature-radio-tile/feature-radio-group';
import { RadioTileScene, SceneWrapper } from 'components/feature-radio-tile/scene-images';
import { ProductUIThemeProvider } from 'components/product-ui/product-ui-theme-context';
import { IntegrationsScene } from 'components/product-ui/scenes/integrations';
import { QuickAddScene } from 'components/product-ui/scenes/quick-add';
import { RecurringDueDatesScene } from 'components/product-ui/scenes/recurring-due-dates';
import { RemindersScene } from 'components/product-ui/scenes/reminders';
import { ScrollHeader } from 'components/scrolling-section/scroll-header/scroll-header';
import { ScrollSection } from 'components/scrolling-section/scroll-section/scroll-section';
import { useLocale } from 'next-intl';
import { useLocalisedContent } from 'services/hooks/use-localised-data';
import { quoteAshley } from './quote-map';
const radioTiles = ['quickAdd', 'recurringDueDates', 'reminders', 'integrations'] as const;
type RadioTile = (typeof radioTiles)[number];
export function SpeedOfThought() {
  const avatarSrc = useLocalisedContent(quoteAshley);
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, 'features-teams');
  const [activeValue, setActiveValue] = useState<RadioTile>('quickAdd');
  function featureRadioTileChangeHandler(value: RadioTile) {
    setActiveValue(value);
  }
  const sceneImages: { [key in RadioTile]: string | ReactElement } = {
    quickAdd: <QuickAddScene />,
    recurringDueDates: <RecurringDueDatesScene />,
    reminders: <RemindersScene />,
    integrations: <IntegrationsScene />
  };
  return <ProductUIThemeProvider theme="red" data-sentry-element="ProductUIThemeProvider" data-sentry-component="SpeedOfThought" data-sentry-source-file="speed-of-thought.tsx">
            <ScrollSection data-sentry-element="ScrollSection" data-sentry-source-file="speed-of-thought.tsx">
                <Box data-sentry-element="Box" data-sentry-source-file="speed-of-thought.tsx">
                    <ScrollSection.StaticSection data-sentry-element="unknown" data-sentry-source-file="speed-of-thought.tsx">
                        <ScrollHeader title={t('speedOfThought')} spotIllustration={{
            src: '/static/features-teams/features_spot1.png',
            offset: {
              x: -136,
              y: -31
            },
            mobileOffset: {
              x: -52,
              y: -42
            }
          }} data-sentry-element="ScrollHeader" data-sentry-source-file="speed-of-thought.tsx">
                            {t('decadeRefining')}
                        </ScrollHeader>

                        <FeatureRadioGroup<RadioTile> onChange={featureRadioTileChangeHandler} value={activeValue} namespace="features-teams" className="mbe-96 md-mbe-192" radioTiles={[{
            titleKey: 'quickAdd',
            bodyKey: 'quickAddBody',
            icon: <PlusSmBase />,
            scene: <RadioTileScene scene={sceneImages.quickAdd} />
          }, {
            titleKey: 'recurringDueDates',
            bodyKey: 'recurringDueDatesBody',
            icon: <Repeat />,
            scene: <RadioTileScene scene={sceneImages.recurringDueDates} />
          }, {
            titleKey: 'reminders',
            bodyKey: 'remindersBody',
            icon: <AlarmClock />,
            scene: <RadioTileScene scene={sceneImages.reminders} />
          }, {
            titleKey: 'integrations',
            bodyKey: 'integrationsBody',
            icon: <ExtensionBase />,
            scene: <RadioTileScene scene={sceneImages.integrations} />
          }]} data-sentry-element="FeatureRadioGroup" data-sentry-source-file="speed-of-thought.tsx" />

                        <ScrollSection.StickyAnchor data-sentry-element="unknown" data-sentry-source-file="speed-of-thought.tsx">
                            <Quote quoteText={t('quoteAshley.quote')} author={t('quoteAshley.name')} role={t('quoteAshley.role')} avatarSrc={`/static/features-teams/avatars/${avatarSrc}`} data-sentry-element="Quote" data-sentry-source-file="speed-of-thought.tsx" />
                        </ScrollSection.StickyAnchor>
                    </ScrollSection.StaticSection>
                </Box>
                <ScrollSection.StickySection data-sentry-element="unknown" data-sentry-source-file="speed-of-thought.tsx">
                    <SceneWrapper scene={sceneImages[activeValue]} data-sentry-element="SceneWrapper" data-sentry-source-file="speed-of-thought.tsx" />
                </ScrollSection.StickySection>
            </ScrollSection>
        </ProductUIThemeProvider>;
}